import React, { useRef } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import type { MulticamAnnotation } from '../../graphqlService';
import { directionCategoryOf, getAnnotationCategory } from '../../utils/annotationCategories';
import { formatTimecode } from '../../utils/timecode';
import { cutLabel, isCut } from './multicamStudioModel';
import { angleColor } from './multicamTheme';

interface DeleteAnnotationDialogProps {
  /** The cut or note to delete; null keeps the dialog closed. */
  annotation: MulticamAnnotation | null;
  /** The studio offers Undo once it's deleted (notes and transcript marks). */
  undoable?: boolean;
  angles: Array<{ angleKey: string; label?: string | null }>;
  fps: number;
  deleting: boolean;
  /** Why the last attempt failed, in plain words. */
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Deleting removes a cut or note on the server straight away (there is no
 * save step to back out of), so it is confirmed first. A failed delete keeps
 * the dialog open with the reason, to retry or cancel.
 */
const DeleteAnnotationDialog: React.FC<DeleteAnnotationDialogProps> = ({
  annotation: current,
  undoable = false,
  angles,
  fps,
  deleting,
  error,
  onCancel,
  onConfirm,
}) => {
  // While it fades out, the dialog keeps showing what it was about.
  const lastRef = useRef(current);
  if (current) lastRef.current = current;
  const annotation = current ?? lastRef.current;
  const cut = annotation ? isCut(annotation) : false;
  const direction = annotation && !cut ? directionCategoryOf(annotation.category) : null;
  let title = 'Delete this note?';
  if (cut) title = 'Delete this cut?';
  else if (direction === 'remove') title = 'Delete this Remove mark?';
  else if (direction === 'short') title = 'Delete this short?';
  const accent = annotation
    ? cut
      ? angleColor(annotation.targetAngleKey)
      : getAnnotationCategory(annotation.category, annotation.color).color
    : undefined;
  return (
    <Dialog
      open={Boolean(current)}
      onClose={() => {
        if (!deleting) onCancel();
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="multicam-delete-title"
    >
      <DialogTitle id="multicam-delete-title" sx={{ fontWeight: 700 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        {annotation && (
          <Box sx={{ mb: 2, pl: 1.5, borderLeft: `3px solid ${accent}` }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
              {formatTimecode(annotation.timelineTimeMs / 1000, fps)}
              {cut ? ` · ${cutLabel(annotation.targetAngleKey, angles)}` : ''}
            </Typography>
            {annotation.text && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                {annotation.text}
              </Typography>
            )}
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          {undoable ? 'It’s removed straight away. You can undo it right after.' : 'It’s removed straight away and can’t be undone.'}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button variant="outlined" onClick={onCancel} disabled={deleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={deleting}>
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteAnnotationDialog;
